import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { PayPalScriptProvider, PayPalButtons, usePayPalScriptReducer } from "@paypal/react-paypal-js";
import { Lock, ShoppingBag } from "lucide-react";
import API, { formatPrice, formatApiError } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useContent } from "@/context/ContentProvider";

const DemoPayButton = ({ items, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  return (
    <button
      disabled={loading}
      onClick={async () => {
        setLoading(true);
        try {
          const { data } = await API.post("/paypal/create-order", { items });
          const { data: pay } = await API.post("/paypal/capture-order", { internal_order_id: data.internal_order_id });
          onSuccess(pay.order_id);
        } catch (e) {
          toast.error(formatApiError(e));
          setLoading(false);
        }
      }}
      data-testid="demo-pay-button"
      className="flex w-full items-center justify-center gap-2 rounded-full bg-[#c9a24b] py-4 text-sm font-semibold text-[#4b3a2f] transition-all hover:bg-[#b8913b]"
    >
      <Lock size={16} /> {loading ? "Procesando…" : "Pagar ahora (modo demo)"}
    </button>
  );
};

const Checkout = () => {
  const { items, subtotal, clear } = useCart();
  const { user } = useAuth();
  const { t } = useContent();
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    API.get("/config").then((r) => setConfig(r.data)).catch(() => setConfig({ paypal_client_id: "", paypal_mode: "demo" }));
  }, []);

  const createOrder = async () => {
    const { data } = await API.post("/paypal/create-order", {
      items: items.map((i) => ({ product_id: i.product_id, title: i.title, price: i.price, quantity: i.quantity })),
    });
    return data.paypal_order_id;
  };

  const onApprove = async (data) => {
    try {
      const res = await API.post("/paypal/capture-order", { paypal_order_id: data.orderID });
      toast.success("¡Pago completado!");
      clear();
      navigate(`/pedido/${res.data.order_id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const paypalSuccess = (orderId) => {
    toast.success("¡Pago completado!");
    clear();
    navigate(`/pedido/${orderId}`);
  };

  if (!user) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-[#faf7f2] px-4 pt-[72px] text-center" data-testid="checkout-gate">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[#f2e9dd] text-[#a47b52]">
          <ShoppingBag size={26} />
        </span>
        <div>
          <h1 className="font-serif-d text-4xl font-semibold text-[#4b3a2f]">{t("msgs.checkout_gate_title", "Crea tu cuenta para continuar")}</h1>
          <p className="mx-auto mt-3 max-w-md text-sm text-[#6b5b52]">
            {t("msgs.checkout_gate_text", "Para proteger tu compra y darte acceso a tus servicios, necesitas una cuenta AWEN. Solo toma un minuto.")}
          </p>
        </div>
        <div className="flex flex-wrap justify-center gap-4">
          <Link to="/registrarse?next=/checkout" data-testid="checkout-go-register" className="rounded-full bg-[#4b3a2f] px-8 py-3.5 text-sm font-semibold text-[#f2e9dd]">
            Crear cuenta
          </Link>
          <Link to="/iniciar-sesion?next=/checkout" data-testid="checkout-go-login" className="rounded-full border border-[#4b3a2f] px-8 py-3.5 text-sm font-semibold text-[#4b3a2f]">
            Iniciar sesión
          </Link>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-[#faf7f2] px-4 pt-[72px] text-center" data-testid="checkout-empty">
        <h1 className="font-serif-d text-4xl font-semibold text-[#4b3a2f]">Tu carrito está vacío</h1>
        <Link to="/catalogo" className="rounded-full bg-[#4b3a2f] px-8 py-3.5 text-sm font-semibold text-[#f2e9dd]">
          Explorar catálogo
        </Link>
      </div>
    );
  }

  const initialOptions = {
    clientId: config?.paypal_client_id || "test",
    currency: "USD",
    intent: "capture",
    components: "buttons",
  };

  return (
    <div className="min-h-screen bg-[#faf7f2] pb-24 pt-[120px]" data-testid="checkout-page">
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <h1 className="font-serif-d text-5xl font-semibold text-[#4b3a2f]">Finalizar compra</h1>
        <div className="mt-10 grid grid-cols-1 gap-10 lg:grid-cols-[1.3fr_1fr]">
          <div className="space-y-6">
            <section className="rounded-3xl border border-[#e8ded4] bg-white p-8">
              <p className="font-mono-d text-[11px] uppercase tracking-[0.2em] text-[#a47b52]">Paso 1 · Tus datos</p>
              <div className="mt-5 grid gap-4 sm:grid-cols-2">
                {[
                  ["Nombre", user.name],
                  ["Correo", user.email],
                  ["WhatsApp", user.whatsapp || "—"],
                  ["Ciudad", user.city || "—"],
                ].map(([l, v]) => (
                  <div key={l} className="rounded-xl bg-[#faf7f2] p-4">
                    <p className="text-[11px] uppercase tracking-wider text-[#a47b52]">{l}</p>
                    <p className="mt-1 text-sm font-medium text-[#4b3a2f]">{v}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="rounded-3xl border border-[#e8ded4] bg-white p-8">
              <p className="font-mono-d text-[11px] uppercase tracking-[0.2em] text-[#a47b52]">Paso 2 · Pago seguro</p>
              <h3 className="mt-3 font-serif-d text-2xl font-semibold text-[#4b3a2f]">
                {config?.paypal_mode === "sandbox" ? "Paga con PayPal" : "Pago con PayPal"}
              </h3>
              <p className="mt-2 text-sm text-[#6b5b52]">
                {config?.paypal_mode === "sandbox"
                  ? "Serás redirigido a PayPal para completar tu compra de forma segura."
                  : "PayPal aún no está configurado (falta Client ID de sandbox). Puedes simular el pago para probar la tienda."}
              </p>
              <div className="mt-6">
                {busy && <p className="mb-4 animate-pulse text-sm text-[#6b5b52]">Creando tu orden…</p>}
                {config?.paypal_mode === "sandbox" ? (
                  <PayPalScriptProvider options={initialOptions}>
                    <PayPalButtons
                      style={{ layout: "vertical", shape: "pill", color: "gold" }}
                      createOrder={async () => {
                        setBusy(true);
                        try {
                          return await createOrder();
                        } catch (e) {
                          toast.error(formatApiError(e));
                          throw e;
                        } finally {
                          setBusy(false);
                        }
                      }}
                      onApprove={async (data) => {
                        try {
                          const res = await API.post("/paypal/capture-order", { paypal_order_id: data.orderID });
                          toast.success("¡Pago completado!");
                          clear();
                          navigate(`/pedido/${res.data.order_id}`);
                        } catch (e) {
                          toast.error(formatApiError(e));
                        }
                      }}
                      onError={() => toast.error("PayPal reportó un error. Inténtalo de nuevo.")}
                      data-testid="paypal-buttons"
                    />
                  </PayPalScriptProvider>
                ) : (
                  <DemoPayButton items={items.map((i) => ({ product_id: i.product_id, title: i.title, price: i.price, quantity: i.quantity }))} onSuccess={paypalSuccess} />
                )}
              </div>
            </section>
          </div>

          <aside className="h-fit rounded-3xl bg-[#4b3a2f] p-8 text-[#f2e9dd] lg:sticky lg:top-24">
            <h2 className="font-serif-d text-2xl font-semibold">Resumen del pedido</h2>
            <div className="mt-6 space-y-4">
              {items.map((i) => (
                <div key={i.product_id} className="flex justify-between gap-4 text-sm">
                  <span className="text-[#cdb79e]">{i.title} <span className="text-[#a47b52]">×{i.quantity}</span></span>
                  <span className="font-medium">{formatPrice(i.price * i.quantity)}</span>
                </div>
              ))}
            </div>
            <div className="mt-6 flex justify-between border-t border-[#c9a24b]/30 pt-5">
              <span className="font-semibold">Total</span>
              <span className="font-serif-d text-2xl font-semibold text-[#c9a24b]" data-testid="checkout-total">{formatPrice(subtotal)} USD</span>
            </div>
            <p className="mt-6 flex items-start gap-2 text-xs leading-relaxed text-[#cdb79e]">
              <Lock size={14} className="mt-0.5 shrink-0" /> Tus datos y tu pago están protegidos. Nunca compartimos tu información.
            </p>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
